import React, { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import axios from 'axios';
import {
  calculateQuestionnaireResults,
  getQuestionnaireType
} from '../utils/questionnaireTypes';
import RadarChartResults from '../components/results/RadarChartResults';
import ScatterPlotResults from '../components/results/ScatterPlotResults';
import {
  Chart as ChartJS,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

ChartJS.register(LinearScale, RadialLinearScale, PointElement, LineElement, Tooltip, Legend, Title);

const AdminReportPage = () => {
  const { userId, questionnaireId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prepare report data for rendering
  const prepareReport = useCallback((data) => {
    try {
      // Get questionnaire configuration
      const config = getQuestionnaireType(data.questionnaireId);
      if (!config) {
        throw new Error(`Configuration not found for questionnaire: ${data.questionnaireId}`);
      }

      // Extract answers (handle different data structures)
      let answers;
      if (data.progress && data.progress.answers) {
        answers = typeof data.progress.answers === 'string'
          ? JSON.parse(data.progress.answers)
          : data.progress.answers;
      } else if (data.answers) {
        answers = typeof data.answers === 'string'
          ? JSON.parse(data.answers)
          : data.answers;
      } else {
        throw new Error('No answers found in questionnaire data');
      }

      // Calculate results
      const results = calculateQuestionnaireResults(data.questionnaireId, answers);

      setReportData({
        config,
        results,
        userData: {
          userId: data.userId,
          userName: data.userName || data.userId,
          lastUpdated: data.lastUpdated,
          status: data.status
        }
      });
      setLoading(false);
    } catch (err) {
      throw new Error(`Failed to prepare report: ${err.message}`);
    }
  }, []);

  // Fetch questionnaire data from API (fallback)
  const fetchQuestionnaireData = useCallback(async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/loadProgress'
        : '/api/loadProgress';

      const response = await axios.get(apiUrl, {
        params: { userId, questionnaireId },
        withCredentials: true,
      });

      if (response.data.success && response.data.progress) {
        // Transform API response to match expected format
        const questionnaireData = {
          userId: userId,
          questionnaireId: questionnaireId,
          progress: response.data.progress,
          lastUpdated: response.data.progress.lastUpdated,
          status: response.data.progress.status,
          userName: 'User' // API doesn't return userName, we'll show userId
        };

        prepareReport(questionnaireData);
      } else {
        throw new Error('No questionnaire data found');
      }
    } catch (err) {
      throw new Error(`Failed to fetch questionnaire: ${err.message}`);
    }
  }, [userId, questionnaireId, prepareReport]);

  // Load data: navigation state first, then API fallback
  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if data was passed via navigation state
        if (location.state?.questionnaireData) {
          console.log('📊 Loading report from navigation state (fast path)');
          prepareReport(location.state.questionnaireData);
        } else {
          console.log('📊 Loading report from API (fallback for bookmarkable URL)');
          await fetchQuestionnaireData();
        }
      } catch (err) {
        console.error('❌ Error loading report:', err);
        setError(err.message || 'Failed to load questionnaire data');
        setLoading(false);
      }
    };

    loadReport();
  }, [fetchQuestionnaireData, prepareReport, location.state]);

  // Render appropriate chart component based on questionnaire type
  const renderResults = () => {
    if (!reportData) return null;

    switch (reportData.config.resultDisplayType) {
      case 'radar':
        return <RadarChartResults results={reportData.results} questionnaireConfig={reportData.config} />;
      case 'scatter':
        return <ScatterPlotResults results={reportData.results} questionnaireConfig={reportData.config} />;
      default:
        return <div>Type de résultat non supporté: {reportData.config.resultDisplayType}</div>;
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="loading">Chargement du rapport...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="card">
          <h2 style={{ color: '#e74c3c' }}>❌ Erreur</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary">
            ← Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="error">Aucun résultat disponible.</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
              📊 {reportData.config.name}
            </h1>
            <div style={{ color: '#7f8c8d', fontSize: '0.95rem' }}>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Utilisateur:</strong> {reportData.userData.userName}
              </p>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>ID:</strong> {reportData.userData.userId}
              </p>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Date:</strong> {reportData.userData.lastUpdated
                  ? new Date(reportData.userData.lastUpdated).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'Non disponible'}
              </p>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Questions:</strong> {reportData.results.totalQuestions}
              </p>
              <p style={{ margin: '0.25rem 0' }}>
                <strong>Statut:</strong> {' '}
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  backgroundColor: reportData.userData.status === 'completed' ? '#27ae60' : '#f39c12',
                  color: 'white'
                }}>
                  {reportData.userData.status === 'completed' ? 'Terminé' : 'En cours'}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="btn btn-secondary"
            style={{ whiteSpace: 'nowrap' }}
          >
            ← Retour
          </button>
        </div>
      </div>

      {/* Results Display */}
      {renderResults()}
    </div>
  );
};

export default AdminReportPage;
